// src/pages/PlayerCharacter.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import apiConfig from '../config/apiConfig';
import './PlayerCharacter.css';
import { calculateAlignmentValue, getAlignmentTitle, getAlignmentColor } from '../data/alignmentTitles';

const PlayerCharacter = () => {
  const { id } = useParams();
  const [character, setCharacter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCharacter = async () => {
      try {
        const response = await fetch(`${apiConfig.getCharacter}${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Failed to fetch character');
        }

        const data = await response.json();
        setCharacter(data);
      } catch (err) {
        setError(err.message || 'An error occurred while fetching character data');
        console.error('Character fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCharacter();
    }
  }, [id]);

  if (loading) {
    return <div className="page-content">Loading character data...</div>;
  }

  if (error) {
    return <div className="page-content error-message">{error}</div>;
  }

  if (!character) {
    return <div className="page-content">Character not found</div>;
  }

  // Format bio with custom color tags
  const formattedBio = character.bio?.replace(
    /\[color=([#a-zA-Z0-9]+)\](.*?)\[\/color\]/g,
    '<span style="color:$1">$2</span>'
  ) || '';

  // Calculate alignment using the proper system
  const alignmentValue = calculateAlignmentValue(
    character.good || 0,
    character.neutral || 0,
    character.evil || 0
  );
  const alignmentTitle = getAlignmentTitle(alignmentValue);
  const alignmentColor = getAlignmentColor(alignmentValue);

  // Calculate character age
  const createdAt = new Date(character.createdAt);
  const now = new Date();
  const ageInDays = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));

  return (
    <div className="page-content player-character">
      <div className="character-header">
        <div className="character-portrait">
          {character.icon ? (
            <img 
              src={`${apiConfig.resources}${character.icon}`} 
              alt={`${character.username}'s portrait`} 
            />
          ) : (
            <div className="no-image">No User Image</div>
          )}
        </div>
        <div className="character-details">
          <h2>{character.username} {character.level !== undefined && `(${character.level})`}</h2>
          <p><strong>Gold:</strong> {character.gold}</p>
          <p><strong>Alignment:</strong> <span style={{ color: alignmentColor }}>{alignmentTitle}</span></p>
          <p><strong>Character Age:</strong> {ageInDays} days old</p>
        </div>
      </div>
      <div className="character-bio">
        <h3>Biography</h3>
        <ReactMarkdown rehypePlugins={[rehypeRaw]}>
          {formattedBio}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default PlayerCharacter;